import { type AccessControlInput, useAccessControl } from '@/hooks/access-control'
import { cn } from '@/lib/utils'
import { Children, type ReactElement, type ReactNode, cloneElement, isValidElement } from 'react'
import { PlanLockBadge } from './PlanLockBadge'
import { PlanLockedPopover } from './PlanLockedPopover'
import { QuotaExceededPopover } from './QuotaExceededPopover'
import { QuotaWarningTooltip } from './QuotaWarningTooltip'

export interface AccessControlledLabels {
	quotaExceeded?: string
	quotaWarning?: string
	planLocked?: string
	denied?: string
}

export interface AccessControlledProps extends AccessControlInput {
	children: ReactNode
	/** Strategy when access is denied by RBAC. Default 'hide'. */
	mode?: 'hide' | 'disable'
	/** Custom fallback rendered in place of children when denied (mode='hide'). */
	fallback?: ReactNode
	/** Optional override copy for the built-in popovers/tooltips. */
	labels?: AccessControlledLabels
	/** Optional className applied to the wrapper span for 'blocked' / 'warning' states. */
	className?: string
	/** When true, plan-locked state shows a PlanLockBadge next to the child. Default true. */
	showLockBadge?: boolean
}

/**
 * Canonical access-control wrapper. Renders based on the composed AccessState:
 *
 *  - allowed               → children
 *  - warning (quota)       → children + hover tooltip with usage info
 *  - blocked (quota-exc.)  → disabled children + popover CTA to billing
 *  - blocked (plan-locked) → disabled children + PlanLockBadge + popover CTA to upgrade
 *  - denied (mode='hide')  → null (or fallback)
 *  - denied (mode='disable') → disabled children (used for in-page regions that should stay visible)
 *  - loading               → children (optimistic; avoids flicker)
 */
export function AccessControlled({
	children,
	mode = 'hide',
	fallback = null,
	labels,
	className,
	showLockBadge = true,
	...input
}: AccessControlledProps) {
	const access = useAccessControl(input)

	if (access.state === 'allowed' || access.state === 'loading') {
		return <>{children}</>
	}

	if (access.state === 'denied') {
		if (mode === 'hide') {
			return <>{fallback}</>
		}
		return disableChildren(children, labels?.denied ?? 'You do not have permission to perform this action')
	}

	if (access.state === 'warning') {
		return (
			<QuotaWarningTooltip
				quotaKey={access.quotaKey}
				used={access.usage.used}
				total={access.usage.total}
				label={labels?.quotaWarning}>
				<span className={cn('inline-flex', className)}>{children}</span>
			</QuotaWarningTooltip>
		)
	}

	if (access.reason === 'quota-exceeded') {
		return (
			<QuotaExceededPopover
				quotaKey={access.quotaKey}
				used={access.usage.used}
				total={access.usage.total}
				billingHref={access.billingHref}
				label={labels?.quotaExceeded}>
				<span className={cn('inline-flex', className)}>{disableChildren(children)}</span>
			</QuotaExceededPopover>
		)
	}

	if (access.reason === 'plan-locked') {
		return (
			<PlanLockedPopover
				requiredPlan={access.requiredPlan}
				currentPlan={access.currentPlan}
				billingHref={access.billingHref}
				label={labels?.planLocked}>
				<span className={cn('inline-flex items-center gap-2', className)}>
					{disableChildren(children)}
					{showLockBadge ? <PlanLockBadge /> : null}
				</span>
			</PlanLockedPopover>
		)
	}

	console.error('AccessControlled: unhandled access state; rendering children unchanged.', access)
	return <>{children}</>
}

/**
 * Injects `disabled` onto single React elements; otherwise wraps in a non-interactive span.
 * Uses aria-disabled as a non-visual fallback for elements that don't accept `disabled`.
 *
 * **`srText`**: When `disableChildren` receives a **single** child, this optional string is written to that
 * element’s `title` (native hover tooltip, and the extra context this path provides for a disabled control).
 * When **`srText`** is omitted, the child’s existing `title` is kept. For multiple children, `disableChildren`
 * wraps them in a `fieldset` and does not apply **`srText`** to that branch.
 */
function disableChildren(children: ReactNode, srText?: string): ReactNode {
	const array = Children.toArray(children)
	if (array.length === 1 && isValidElement(array[0])) {
		const child = array[0] as ReactElement<{
			disabled?: boolean
			'aria-disabled'?: boolean
			className?: string
			title?: string
			tabIndex?: number
			onClick?: (event: unknown) => void
			onMouseDown?: (event: unknown) => void
			onPointerDown?: (event: unknown) => void
			onKeyDown?: (event: unknown) => void
			onKeyUp?: (event: unknown) => void
			onFocus?: (event: unknown) => void
		}>
		if (typeof child.type === 'string') {
			return cloneElement(child, {
				className: cn(child.props.className, 'pointer-events-none opacity-50'),
				disabled: true,
				'aria-disabled': true,
				tabIndex: -1,
				title: srText ?? child.props.title,
				onClick: undefined,
				onMouseDown: undefined,
				onPointerDown: undefined,
				onKeyDown: undefined,
				onKeyUp: undefined,
				onFocus: undefined,
			})
		}
		return (
			<fieldset
				disabled
				aria-disabled='true'
				className={cn('pointer-events-none opacity-50 border-0 p-0 m-0 inline-flex', child.props.className)}
				title={srText ?? child.props.title}>
				{cloneElement(child, {
					disabled: true,
					'aria-disabled': true,
					tabIndex: -1,
					onClick: undefined,
					onMouseDown: undefined,
					onPointerDown: undefined,
					onKeyDown: undefined,
					onKeyUp: undefined,
					onFocus: undefined,
				})}
			</fieldset>
		)
	}
	return (
		<fieldset disabled aria-disabled='true' className='pointer-events-none opacity-50 border-0 p-0 m-0'>
			{children}
		</fieldset>
	)
}
